import type { components } from './generated/schema'

/** One entry of a 422's real envelope — `HTTPValidationError.detail`,
 *  `schema.d.ts:4678-4681`, always an ARRAY. Read from the generated schema
 *  rather than hand-written, per this repo's own rule. */
export type ValidationError = components['schemas']['ValidationError']

function isValidationError(value: unknown): value is ValidationError {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as { msg?: unknown; loc?: unknown }
  return typeof candidate.msg === 'string' && Array.isArray(candidate.loc)
}

/**
 * The real 422 envelope's entries, or `[]` when `body` is not that shape.
 *
 * **`detail` is only ever an ARRAY here.** `HTTPValidationError.detail:
 * ValidationError[]` (`schema.d.ts:4678-4681`) is FastAPI's genuine
 * validation-failure envelope. A bare `{"detail": "..."}` STRING is a
 * different envelope entirely — FastAPI's default `HTTPException`, which is
 * what 400/403/404 send across this app (`rowFailure.ts`'s own `detailOf`,
 * and the two forms' own `messageFor`). A string `detail` reaching this
 * function returns `[]`, which is correct: those statuses are read by their
 * own status-based mapping, and this function is only ever consulted for a
 * 422.
 *
 * Each entry is validated defensively (`msg` a string, `loc` an array) rather
 * than merely cast — the body is `unknown` off the wire, and a malformed
 * entry must not throw partway through a `.map`.
 */
export function validationErrors(body: unknown): ValidationError[] {
  if (typeof body !== 'object' || body === null || !('detail' in body)) return []
  const { detail } = body as { detail: unknown }
  if (!Array.isArray(detail)) return []
  return detail.filter(isValidationError)
}

/** A leading wrapper Pydantic still prepends to a `@field_validator`'s own
 *  message when it re-raises as a `ValueError`/`AssertionError` — the
 *  `RequestValidationError` handler backend #407 added strips this
 *  CENTRALLY, so on a current dev/stag response there is nothing here to
 *  strip. Kept anyway: it is belt-and-braces against an older deployment, it
 *  is free when it matches nothing, and it is the only defence against any
 *  validator that raises OUTSIDE that one handler. See issue #473. */
const STRIPPABLE_PREFIXES = ['Value error, ', 'Assertion failed, ']

/**
 * Normalises one validation `msg` for display.
 *
 * **Strips the prefix only if it is actually there — never unconditionally.**
 * "Everything after the first comma" was rejected for this exact reason: it
 * eats the front of a legitimate message the moment the string contains any
 * other comma, and it goes wrong silently the instant the backend stops
 * sending the prefix (which it now does, backend #407). Matching the literal
 * prefix means this function is correct against an old backend, a new one,
 * and a rollback between them, with no edit ever needed again.
 */
export function normaliseValidationMessage(msg: string): string {
  for (const prefix of STRIPPABLE_PREFIXES) {
    if (msg.startsWith(prefix)) return msg.slice(prefix.length)
  }
  return msg
}

/** The field name one entry's `loc` names — its own last segment, when that
 *  segment is a string. (`loc` also carries numeric array indices for a
 *  repeated field, which name no single control on a form.) */
export function fieldNameOf(entry: ValidationError): string | undefined {
  const last = entry.loc.at(-1)
  return typeof last === 'string' ? last : undefined
}

/** One 422 entry with no control of its own on the caller's form — its own
 *  `loc` field name, when it has one, and its normalised `msg`. Deliberately
 *  UNJOINED and UNLABELLED: separators, human field labels and how many to
 *  show are rendering decisions, and this is `src/api/` — parsing lives
 *  here, copy does not (architecture review, issue #473 round 2). */
export type OtherEntry = { field: string | undefined; message: string }

export type RoutedValidation = {
  /** Every entry for a field in `knownFields`, normalised — never just the
   *  first. Absent for a field with no entry. A caller with a single-string
   *  error slot (`Field`'s `error` prop) joins its own array; nothing here
   *  decides that separator for it. */
  fieldMessages: Map<string, string[]>
  /** Every entry whose field is not in `knownFields`, or has no named field
   *  at all — one entry per `ValidationError`, never collapsed. */
  otherEntries: OtherEntry[]
}

/**
 * Routes every 422 entry in `body` to the named field it targets, when the
 * caller's form has a control for that field (`knownFields`), and to
 * `otherEntries` otherwise.
 *
 * **Every entry contributes, and none are merged into a string here.** Issue
 * #473's own defect was `detail[0]`-only rendering — a route that dropped
 * anything, INCLUDING by joining two entries into one string a caller can no
 * longer tell apart, would reintroduce a version of it. A caller that wants
 * one combined message joins `fieldMessages`'s own array or `otherEntries`
 * itself.
 */
export function routeValidationErrors(body: unknown, knownFields: readonly string[]): RoutedValidation {
  const entries = validationErrors(body)
  const fieldMessages = new Map<string, string[]>()
  const otherEntries: OtherEntry[] = []
  for (const entry of entries) {
    const message = normaliseValidationMessage(entry.msg)
    const field = fieldNameOf(entry)
    if (field !== undefined && knownFields.includes(field)) {
      const list = fieldMessages.get(field) ?? []
      list.push(message)
      fieldMessages.set(field, list)
    } else {
      otherEntries.push({ field, message })
    }
  }
  return { fieldMessages, otherEntries }
}
